import type { ReactNode } from 'react'
import { useContext, useMemo } from 'react'

import { createSharedContext } from '@twistopayments/design-system/utils'

import type { BannerSize, BannerVariant } from './banner'

/**
 * Context
 */

type BannerValue =
  | {
      size: BannerSize
      variant: BannerVariant
      hasAction: boolean
    }
  | undefined

const Context = createSharedContext<BannerValue>('components/molecules/banner/banner.context#0', undefined)

/**
 * Provider
 */

type Props = {
  children: ReactNode
  size: BannerSize
  variant: BannerVariant
  hasAction: boolean
}

export const BannerContextProvider = ({
  children,
  size,
  variant,
  hasAction,
}: Props) => {
  const contextValue: BannerValue = useMemo(
    () => ({ size, variant, hasAction }),
    [size, variant, hasAction]
  )

  return <Context.Provider value={contextValue}>{children}</Context.Provider>
}

/**
 * Hook
 */

export const useBannerContext = () => {
  const context = useContext(Context)

  if (context === undefined) {
    throw new Error(
      'useBannerContext must be used within a BannerContextProvider'
    )
  }

  return context
}
